import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { createDonationCheckoutSession, getMoneyMotionEnabled } from '#bv/lib/moneymotion'
import { createNowPaymentsDonationPayment, getNowPaymentsEnabled } from '#bv/lib/nowpayments'
import { stashMmCheckoutContext } from '#bv/lib/mmCheckoutSession'

export const Route = createFileRoute('/donation')({
  component: DonationPage,
})

function DonationPage() {
  const navigate = useNavigate()
  const presets = useMemo(() => [5, 10, 25, 50, 100] as const, [])
  const [amountMode, setAmountMode] = useState<'preset' | 'custom'>('preset')
  const [presetAmount, setPresetAmount] = useState<(typeof presets)[number]>(10)
  const [customAmount, setCustomAmount] = useState('')
  const [note, setNote] = useState('')
  const [email, setEmail] = useState('')
  const [mmEnabled, setMmEnabled] = useState<boolean | null>(null)
  const [cryptoEnabled, setCryptoEnabled] = useState<boolean | null>(null)
  const [payCurrency, setPayCurrency] = useState('ltc')
  const [payTab, setPayTab] = useState<'card' | 'crypto'>('card')
  const [loading, setLoading] = useState(false)
  const [cryptoLoading, setCryptoLoading] = useState(false)
  const [error, setError] = useState('')

  const amount = useMemo(() => {
    if (amountMode === 'custom') {
      const n = Number(customAmount.replace(/[^0-9.]/g, ''))
      if (!Number.isFinite(n) || n <= 0) return null
      return Math.round(n * 100) / 100
    }
    return presetAmount
  }, [amountMode, customAmount, presetAmount])

  const canContinue = amount != null && amount > 0

  useEffect(() => {
    getMoneyMotionEnabled()
      .then(setMmEnabled)
      .catch(() => setMmEnabled(false))
    getNowPaymentsEnabled()
      .then(setCryptoEnabled)
      .catch(() => setCryptoEnabled(false))
  }, [])

  useEffect(() => {
    if (mmEnabled === null || cryptoEnabled === null) return
    if (mmEnabled) setPayTab('card')
    else if (cryptoEnabled) setPayTab('crypto')
  }, [mmEnabled, cryptoEnabled])

  const startCardDonation = async () => {
    if (!canContinue || amount == null) return
    setError('')
    setLoading(true)
    try {
      const result = await createDonationCheckoutSession({
        data: { amountUsd: amount, email: email.trim(), note: note.trim() || undefined },
      })
      if (!result?.checkoutUrl) throw new Error('Checkout URL not returned')
      stashMmCheckoutContext(result.checkoutSessionId, email.trim(), `Donation $${amount.toFixed(2)}`)
      window.location.href = result.checkoutUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to start donation checkout.')
    } finally {
      setLoading(false)
    }
  }

  const startCryptoDonation = async () => {
    if (!canContinue || amount == null) return
    setError('')
    setCryptoLoading(true)
    try {
      const p = await createNowPaymentsDonationPayment({
        data: { amountUsd: amount, email: email.trim(), note: note.trim() || undefined, payCurrency },
      })
      if (!p.paymentId) throw new Error('No payment ID returned.')
      await navigate({
        to: '/checkout/crypto/$paymentId',
        params: { paymentId: p.paymentId },
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start crypto donation.')
    } finally {
      setCryptoLoading(false)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (payTab === 'card' && mmEnabled) void startCardDonation()
    else if (payTab === 'crypto' && cryptoEnabled) void startCryptoDonation()
  }

  return (
    <div className="nv-home">
      <section className="nv-hero">
        <div className="nv-wrap">
          <div className="nv-hero3">
            <div className="nv-hero3-spot" aria-hidden />
            <div className="nv-hero3-frame">
              <div className="nv-grain" aria-hidden />
              <div className="nv-hero3-kicker">Support</div>
              <h1 className="nv-hero3-h">
                <span className="nv-hero3-h-line">Help keep PulseDigital</span>
                <span className="nv-hero3-h-line">online and improving.</span>
              </h1>
              <p className="nv-hero3-sub">
                Donations help cover hosting, infrastructure, tooling, and the work that keeps Pulse Shop fast and reliable.
              </p>

              <div className="nv-donate">
                <div className="nv-donate-head">
                  <div className="nv-donate-k">Choose amount</div>
                  <div className="nv-donate-h">Support hosting & improvements</div>
                </div>

                <div className="nv-donate-row" role="group" aria-label="Donation amount">
                  {presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`nv-donate-chip ${amountMode === 'preset' && presetAmount === p ? 'nv-donate-chip--on' : ''}`}
                      onClick={() => {
                        setAmountMode('preset')
                        setPresetAmount(p)
                      }}
                    >
                      ${p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`nv-donate-chip ${amountMode === 'custom' ? 'nv-donate-chip--on' : ''}`}
                    onClick={() => setAmountMode('custom')}
                  >
                    Custom
                  </button>
                </div>

                {amountMode === 'custom' ? (
                  <div className="nv-donate-custom">
                    <label className="nv-donate-label" htmlFor="donation-amount">
                      Custom amount (USD)
                    </label>
                    <input
                      id="donation-amount"
                      inputMode="decimal"
                      placeholder="e.g. 12.50"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="nv-donate-input"
                    />
                  </div>
                ) : null}

                <div className="nv-donate-custom">
                  <label className="nv-donate-label" htmlFor="donation-email">
                    Email (required)
                  </label>
                  <input
                    id="donation-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="nv-donate-input"
                  />
                </div>

                <div className="nv-donate-custom">
                  <label className="nv-donate-label" htmlFor="donation-note">
                    Note (optional)
                  </label>
                  <input
                    id="donation-note"
                    placeholder="Anything you want us to know…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="nv-donate-input"
                  />
                </div>

                <div className="nv-donate-actions">
                  <form onSubmit={submit} className="w-full">
                    <div
                      className="mb-3 grid gap-2"
                      style={{ gridTemplateColumns: cryptoEnabled && mmEnabled ? '1fr 1fr' : '1fr' }}
                    >
                      {mmEnabled ? (
                        <button
                          type="button"
                          className={`nv-donate-chip ${payTab === 'card' ? 'nv-donate-chip--on' : ''}`}
                          onClick={() => setPayTab('card')}
                        >
                          Card
                        </button>
                      ) : null}
                      {cryptoEnabled ? (
                        <button
                          type="button"
                          className={`nv-donate-chip ${payTab === 'crypto' ? 'nv-donate-chip--on' : ''}`}
                          onClick={() => setPayTab('crypto')}
                        >
                          Crypto
                        </button>
                      ) : null}
                    </div>

                    {payTab === 'crypto' && cryptoEnabled ? (
                      <div className="nv-donate-custom" style={{ marginTop: 10 }}>
                        <label className="nv-donate-label" htmlFor="donation-crypto">
                          Pay with
                        </label>
                        <select
                          id="donation-crypto"
                          value={payCurrency}
                          onChange={(e) => setPayCurrency(e.target.value)}
                          className="nv-donate-input"
                          aria-label="Cryptocurrency"
                        >
                          <option value="ltc">Litecoin (LTC)</option>
                          <option value="btc">Bitcoin (BTC)</option>
                          <option value="eth">Ethereum (ETH)</option>
                          <option value="doge">Dogecoin (DOGE)</option>
                          <option value="sol">Solana (SOL)</option>
                          <option value="usdt">USDT (ERC20)</option>
                        </select>
                      </div>
                    ) : null}

                    <button
                      type="submit"
                      className="nv-btn nv-btn-primary nv-btn-shine w-full"
                      disabled={!canContinue || !email.trim() || loading || cryptoLoading}
                      style={{ marginTop: 10 }}
                    >
                      {loading || cryptoLoading
                        ? 'Starting checkout…'
                        : payTab === 'crypto'
                          ? `Donate $${amount?.toFixed(2) ?? '—'} with crypto`
                          : `Donate $${amount?.toFixed(2) ?? '—'} with card`}
                    </button>

                    {error ? (
                      <div
                        className="mt-3 rounded-xl px-3 py-2 text-xs"
                        style={{ background: 'rgba(0, 191, 255, 0.08)', border: '1px solid rgba(0,191,255,0.18)', color: '#ffd2ee' }}
                      >
                        {error}
                      </div>
                    ) : null}

                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                      <a
                        href="https://discord.gg/tGxhDfRkdE"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nv-btn nv-btn-discord"
                      >
                        <DiscordIcon size={16} white className="shrink-0" /> Discord
                      </a>
                      <Link to="/contact" className="nv-btn nv-btn-ghost">
                        Contact about sponsoring
                      </Link>
                    </div>
                  </form>
                </div>
              </div>

              <div className="nv-hero3-trustline">
                Prefer something formal? Use <Link to="/contact" className="nv-hero3-link">Contact</Link> for sponsorships and invoicing.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-alt">
        <div className="nv-wrap">
          <div className="nv-final-card">
            <div className="nv-final-k">What donations fund</div>
            <div className="nv-final-h">Reliability first.</div>
            <div className="nv-final-s">
              Server costs, monitoring, anti-fraud improvements, better listings, and support tooling.
            </div>
            <div className="nv-featured-cta">
              <a href="https://discord.gg/tGxhDfRkdE" target="_blank" rel="noopener noreferrer" className="nv-btn nv-btn-discord">
                Open Discord
              </a>
              <Link to="/terms" className="nv-btn nv-btn-ghost">
                Terms
              </Link>
              <Link to="/refund" className="nv-btn nv-btn-ghost">
                Refunds
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

